/**
 * Chat session lifecycle shared by the HTTP client, SignalR and the chat page.
 * A 401 on a request that carried the chat token (or SignalR refusing the token) means the session is over:
 * listeners (the chat page) send the guest back to sign-in instead of leaving the chat stuck.
 */
type SessionListener = () => void;

const listeners = new Set<SessionListener>();

export const onSessionExpired = (listener: SessionListener): (() => void) => {
   listeners.add(listener);
   return () => {
      listeners.delete(listener);
   };
};

export const notifySessionExpired = (): void => {
   listeners.forEach((listener) => {
      try {
         listener();
      } catch (err) {
         console.error('Session-expired listener failed:', err);
      }
   });
};

/** A token this close to its expiry is treated as expired (the server also allows some clock skew). */
const EXPIRY_MARGIN_MS = 30 * 1000;

/** Whether a JWT `exp` (seconds since the epoch) has passed, or is missing. */
export const isTokenExpired = (exp: number | undefined, nowMs: number = Date.now()): boolean =>
   !exp || exp * 1000 <= nowMs + EXPIRY_MARGIN_MS;

/** Whether an error (HTTP or SignalR negotiate) means the server rejected the token. */
export const isUnauthorizedError = (error: unknown): boolean => {
   const e = error as { status?: number; statusCode?: number; response?: { status?: number }; message?: string } | null | undefined;
   if (!e) return false;
   if (e.status === 401 || e.statusCode === 401 || e.response?.status === 401) return true;
   return /status code '401'|\b401\b.*unauthori[sz]ed|unauthori[sz]ed.*\b401\b/i.test(String(e.message ?? ''));
};
